// "Why is this a zone?": the evidence behind an order block and its life so far, opened by clicking
// the zone on the chart. Times are candle start times; "at 10:15" for a close means the 10:10 candle.
import { XIcon } from 'lucide-react';
import { NSE, OB_RULES_VERSION, zoneState, type Analysed, type OrderBlock } from '@planb/core';
import { cn } from '@/lib/utils';
import { STATE_TEXT, zoneStateLabel } from './stock-sections';

const px = NSE.formatPrice;
const at = (i: number) => NSE.timeLabel(i);
const closeAt = (i: number) => NSE.timeLabel(i + 1);

export function ZoneEvidence({ o, a, onClose }: { o: OrderBlock; a: Analysed; onClose: () => void }) {
  const bull = o.type === 'bull';
  const st = zoneState(o);
  const sweep = a.sweeps.filter((w) => w.type === o.type && w.idx >= o.idx - 6 && w.idx <= o.confirmIdx).pop();
  const row = (k: string, v: React.ReactNode) => (
    <div className="grid grid-cols-[96px_1fr] gap-2 border-b border-border/60 py-1.5 last:border-b-0">
      <dt className="text-muted-foreground">{k}</dt><dd className="text-ink">{v}</dd>
    </div>
  );
  return (
    <div role="dialog" aria-label="Why this zone" className="absolute top-9 right-3 z-20 w-[340px] max-w-[calc(100%-24px)] rounded-xl border bg-card p-3.5 text-[12.5px] shadow-xl">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-heading text-[14px] font-semibold text-ink">{bull ? 'Bullish' : 'Bearish'} order block · {at(o.idx)}</div>
          <span className={cn('mt-1 inline-flex h-5 items-center rounded-full px-2 text-[11px] font-semibold', STATE_TEXT[st].cls)}>{zoneStateLabel(st, o.touches.length)}</span>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="grid size-7 place-items-center rounded-md text-muted-foreground hover:bg-tint hover:text-ink"><XIcon className="size-4" /></button>
      </div>
      <dl className="mt-2">
        {row('Source candle', <>{at(o.idx)}: the last {bull ? 'red' : 'green'} candle before the move</>)}
        {row('Zone', o.wide
          ? <>{px(o.bottom)}–{px(o.top)} (its body). The full candle, {px(o.fullBottom)}–{px(o.fullTop)}, was {((o.fullTop - o.fullBottom) / o.unit).toFixed(1)}× a usual candle, too wide to be precise.</>
          : <>{px(o.bottom)}–{px(o.top)} (the candle’s full range)</>)}
        {row('Move', o.displacement > 0
          ? <>closed {o.displacement.toFixed(1)}× a usual candle beyond it{o.gap ? ', leaving a price gap' : ''}</>
          : <>left a price gap, but hadn’t closed beyond the candle when the zone was confirmed (a weaker sign)</>)}
        {row('Structure', <>confirmed at {closeAt(o.confirmIdx)}: a close {bull ? 'above' : 'below'} the {o.bosKind === 'swing' ? `swing ${bull ? 'high' : 'low'}` : `opening-range ${bull ? 'high' : 'low'}`} {px(o.bosLevel)}</>)}
        {row('Volume', o.volRatio == null ? 'too early in the day to compare' : <>{o.volRatio.toFixed(1)}× the average candle before it today</>)}
        {row('Liquidity', sweep ? <>{sweep.failed ? 'failed sweep' : 'swept'} {sweep.name} at {at(sweep.idx)} just before</> : 'no sweep just before')}
        {row('Visits', o.touches.length === 0 ? 'none since it was confirmed' : (
          <ul className="grid gap-0.5">
            {o.touches.map((tc) => (
              <li key={tc.idx}>{at(tc.idx)}: {tc.outcome === 'reacted' ? `reacted (close at ${closeAt(tc.resolvedIdx!)} moved away)` : tc.outcome === 'testing' ? 'testing now' : o.mitigated && tc.resolvedIdx === o.endIdx ? 'price went on through: the zone broke' : 'no reaction within three candles'}</li>
            ))}
          </ul>
        ))}
        {o.late && row('Timing', <>confirmed in the last 30 minutes of trading: little time left for it to matter today</>)}
        {a.thin && row('Liquidity', <>thin: a usual 5-minute candle trades under ₹40 lakh, so a single order can make a candle, and a zone</>)}
        {row('Breaks if', o.mitigated
          ? <>broke at {closeAt(o.endIdx!)}: a close {bull ? 'below' : 'above'} {px(bull ? o.bottom : o.top)}</>
          : <>a 5-minute candle closes {bull ? 'below' : 'above'} {px(bull ? o.bottom : o.top)}</>)}
      </dl>
      <p className="mt-2 text-[11px] leading-snug text-muted-foreground">
        A usual candle here is {px(o.unit)} (from this stock’s usual day range). Evidence for a price zone, not a prediction. Rules {OB_RULES_VERSION}.
      </p>
    </div>
  );
}
